import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableColumnDefinition,
  TableColumnSizingOptions,
  TableHeader,
  TableHeaderCell,
  TableRow,
  createTableColumn,
  useTableColumnSizing_unstable,
  useTableFeatures,
  Checkbox,
  Button,
  makeStyles,
} from "@fluentui/react-components";
import { EditRegular, DeleteRegular } from "@fluentui/react-icons";
import kontakteData from "../data/viewKontakteÜbersicht.json";
import { ContactDetailsFormValues } from "../types/Contact";
import { CommandBar } from "../components/CommandBar";
import { Star16Filled, Star16Regular } from "@fluentui/react-icons";
import PhoneCell from "../components/PhoneCell";

const useStyles = makeStyles({
  stripedRow: {
    backgroundColor: "#f2f2f2",
  },
  cellEllipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

function Stars({
  value,
  max = 3,
  title,
}: {
  value: number;
  max?: number;
  title?: string;
}) {
  const v = Math.max(0, Math.min(value ?? 0, max));
  return (
    <span
      aria-label={`${v} von ${max} Sternen`}
      title={title ?? `${v} von ${max} Sternen`}
      style={{
        display: "inline-flex",
        gap: 2,
        lineHeight: 0,
        verticalAlign: "middle",
      }}
    >
      {Array.from({ length: max }).map((_, i) =>
        i < v ? (
          <Star16Filled key={i} style={{ color: "gold" }} />
        ) : (
          <Star16Regular key={i} />
        ),
      )}
    </span>
  );
}

const CompanyContactTable: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [kontakte, setKontakte] =
    useState<ContactDetailsFormValues[]>(kontakteData);
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");

  // Spalten-Definition mit createTableColumn
  const columns: TableColumnDefinition<ContactDetailsFormValues>[] =
    React.useMemo(
      () => [
        createTableColumn<ContactDetailsFormValues>({
          columnId: "select",
          renderHeaderCell: () => <></>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Name",
          renderHeaderCell: () => <>Name</>,
          renderCell: (item) => <>{item.lastname}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Branche",
          renderHeaderCell: () => <>Branche</>,
          renderCell: (item) => <>{item.branche}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Ort",
          renderHeaderCell: () => <>Ort</>,
          renderCell: (item) => <>{item.ort}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Straße",
          renderHeaderCell: () => <>Straße</>,
          renderCell: (item) => <>{item.strasse}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Telefon",
          renderHeaderCell: () => <>Telefon</>,
          renderCell: (item) => <>{item.telefon}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Ansprechpartner",
          renderHeaderCell: () => <>Ansprechpartner</>,
          renderCell: (item) => <>{item.ansprechpartner}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Rolle Ansprechpartner",
          renderHeaderCell: () => <>Rolle Ansprechpartner</>,
          renderCell: (item) => <>{item.role_ansprechpartner}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Kontakttyp",
          renderHeaderCell: () => <>Kontakttyp</>,
          renderCell: (item) => <>{item.typ}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Kontakttyp",
          renderHeaderCell: () => <>Kontakttyp</>,
          renderCell: (item) => <>{item.typ}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Kontaktrelevanz",
          renderHeaderCell: () => <>Kontaktrelevanz</>,
          renderCell: (item) => <>{item.relevanz}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "offene Vorgange",
          renderHeaderCell: () => <>offene Vorgange</>,
          renderCell: (item) => <>{item.open_tasks}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Kontaktverantwortlicher",
          renderHeaderCell: () => <>Kontaktverantwortlicher</>,
          renderCell: (item) => <>{item.kundenberater}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Kontaktwert",
          renderHeaderCell: () => <>Kontaktwert</>,
          renderCell: (item) => <>{item.value}</>,
        }),
      ],
      [],
    );

  // ColumnSizingOptions definieren
  const columnSizingOptions: TableColumnSizingOptions = React.useMemo(
    () => ({
      select: { defaultWidth: 48, minWidth: 48 },
      RisikoOrt: { defaultWidth: 340, minWidth: 150 },
      Zweig: { defaultWidth: 120, minWidth: 80 },
      Vertragsnummer: { defaultWidth: 195, minWidth: 100 },
      KontaktName: { defaultWidth: 255, minWidth: 120 },
      actions: { defaultWidth: 100, minWidth: 80 },
    }),
    [],
  );

  const filteredKontakte = kontakte.filter((k) =>
    k.lastname!.toLowerCase().includes(search.toLowerCase()),
  );
  // Table-Features inklusive ColumnSizing
  const { getRows, tableRef, columnSizing_unstable } = useTableFeatures(
    { columns, items: filteredKontakte },
    [useTableColumnSizing_unstable({ columnSizingOptions })],
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };
  const handleNew = () => {
    navigate("/kunde/new");
  };
  // Auswahl-Handler
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Navigation zu Edit
  const handleEdit = (item: ContactDetailsFormValues) => {
    navigate(`/kontakte/edit/${item.kontaktId}`, { state: { k: item } });
  };

  // Löschen
  const handleDelete = (id: number) => {
    setKontakte((prev) => prev.filter((k) => k.kontaktId !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleEditClick = () => {
    if (selectedIds.size === 1) {
      const it = selectedIds.values();
      const key = it.next();
      handleEdit(kontakte.filter((el) => el.kontaktId === key.value)[0]);
    }
  };

  const rows = getRows();

  return (
    <div style={{ fontSize: "14px", width: "120vw", overflowX: "auto" }}>
      <CommandBar
        header="Suche"
        onSearchChange={handleSearchChange}
        onEditClick={handleEditClick}
        onNewClick={handleNew}
      />
      <Table
        ref={tableRef}
        aria-label="Risiken-Tabelle"
        {...columnSizing_unstable.getTableProps()}
      >
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHeaderCell
                key={col.columnId}
                {...columnSizing_unstable.getTableHeaderCellProps(col.columnId)}
              >
                {col.columnId === "select" ? null : col.renderHeaderCell()}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ item }, i) => (
            <TableRow
              key={item.kontaktId}
              className={i % 2 === 1 ? classes.stripedRow : undefined}
            >
              <TableCell
                {...columnSizing_unstable.getTableCellProps("select")}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={selectedIds.has(item.kontaktId)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(item.kontaktId)}
                />
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Name")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.lastname}>
                  {item.lastname}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Branche")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.branche}>
                  {item.branche}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Ort")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.ort}>
                  {item.ort}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Straße")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.strasse}>
                  {item.strasse}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Telefon")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.telefon}>
                  <PhoneCell
                    item={item}
                    phones={[
                      {
                        id: "1",
                        label: "Haupt",
                        number: "+49 2423 987654",
                        primary: true,
                      },
                      {
                        id: "2",
                        label: "Gesch.",
                        number: "+49 2423 987654",
                        primary: false,
                      },
                    ]}
                  />
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Ansprechpartner")}
                onClick={() => handleEdit(item)}
              >
                <div
                  className={classes.cellEllipsis}
                  title={item.ansprechpartner}
                >
                  {item.ansprechpartner}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps(
                  "Rolle Ansprechpartner",
                )}
                onClick={() => handleEdit(item)}
              >
                <div
                  className={classes.cellEllipsis}
                  title={item.role_ansprechpartner}
                >
                  {item.role_ansprechpartner}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Kontakttyp")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.typ + ""}>
                  {item.typ === 0 ? "Interessent" : "Kunde"}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Kontaktrelevanz")}
                onClick={() => handleEdit(item)}
              >
                <div
                  className={classes.cellEllipsis}
                  title={item.relevanz + ""}
                >
                  <Stars value={item.relevanz!} />
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("offene Vorgange")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={"offene Vorgange"}>
                  {item.open_tasks?.join(", ")}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps(
                  "Kontaktverantwortlicher",
                )}
                onClick={() => handleEdit(item)}
              >
                <div
                  className={classes.cellEllipsis}
                  title={item.kundenberater}
                >
                  {item.kundenberater}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Kontaktwert")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.value}>
                  {item.value}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default CompanyContactTable;
